import withErrorBoundary from "src/components/errorBoundary/withErrorBoundary";
import { cloneDeep, get, has, includes, isEmpty, isEqual, map, set, size } from "lodash";
import cx from "classnames";
import styles from "./styles.module.scss";
import { TabBar } from "src/components/tabbar";
import React, { useEffect, useRef, useState } from "react";
import { viewerSettingsEnum, listOfTabs, tabOrders, defaultViewersEnum } from "src/types/settings/viewerSettings";
import Actions from "src/components/actionbar";
import { useAccountFetch, useUpdateAccount } from "src/query/settings/viewerSettings";
import {DefaultViewer} from "./components/defaultViewer";
import Button from "src/components/button";
import { animated, useSpring, useTransition } from "@react-spring/web";
import { EmailViewer } from "./components/emailViewer";
import { ThemeAndGatingViewer } from "./components/themeAndGatingViewer";
import { Account } from "src/types/accounts";
import { LibrarySettings } from "./components/librarySettings";
import { convertBannerToBase64 } from "src/services/accountManagement";
import { MediaFile } from "src/types/media";
import { Banner } from "src/types/common";
import { useDispatch } from "react-redux";
import { updatingAccountSucceeded } from "src/redux/actions/accounts";
import { toast } from "react-toastify";
import Toast from "src/components/toast";
import DefaultFooterMessageTemplate from "src/templates/DefaultFooterMessage.handlebars.js";

import localizedSettings, { account } from 'src/localization/english/pages/settings';
import localizedCommons from 'src/localization/english/commons';

type TabContentProps = {
  index: number;
  selected: number;
  children: any;
}

function ViewerSettings() {

  const { data: account, isLoading: isAccountSettingsLoading } = useAccountFetch();
  const { mutate: updateAccount, isPending } = useUpdateAccount();

  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [accountSettings, setAccountSettings] = useState<Partial<Account>>({});
  const initialAccountSettingsRef = useRef<Partial<Account>>({});
  const [showEditor, setShowEditor] = useState<boolean>(true);

  const isFavIconModified = !!account && !isEmpty(accountSettings) && !isEqual(accountSettings?.favIcon, account?.favIcon);
  const isBannerModified = !!account && !isEmpty(accountSettings) && !isEqual(accountSettings?.librarySettings?.banner?.background, account?.librarySettings?.banner?.background);
  const [resetEditors, setResetEditors] = useState(false); 
  const resetEditorsRef = useRef<any>(null);

  const previewActionTransitions = useTransition(showEditor, {
    from: { opacity: 0, transform: "translateY(-10px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(-10px)" },
    config: { tension: 220, friction: 20 },
  });

  // Methods
  const defaultEmailContent = () => { // default email content template
    return  DefaultFooterMessageTemplate({
          librarySettings: get(account, "librarySettings", {}),
    });
  }

  const getAccountSettings = (accountSettings: any) => { // default values for library settings key & pairs if not present

    const ndaText = get(accountSettings, "librarySettings.ndaText", {});
    const gatingText = get(accountSettings, "librarySettings.gatingText", {});
    const passwordText = get(accountSettings, "librarySettings.passwordText", {});
    const otpText = get(accountSettings, "librarySettings.otpText", {});

    // Localized strings
    const localizedViewerSettings = localizedSettings.account.viewer_settings.library_settings;
    const localizedStringPasswordProtected = localizedViewerSettings.password_protected;
    const localizedStringGatingCustomization = localizedViewerSettings.gating_customization;
    const localizedStringNdaCustomization = localizedViewerSettings.nda_customization;
    const localizedStringOtp = localizedViewerSettings.otp;

    return {
      ...accountSettings,
      librarySettings: {
        ...get(accountSettings, "librarySettings", {}),
        // Default Values
        primaryColor: get(accountSettings, "librarySettings.primaryColor") || "#e51058",
        buttonTextColor:
          get(accountSettings, "librarySettings.buttonTextColor") || "#717274",
        shareText:
          get(accountSettings, "librarySettings.shareText") ||
          localizedViewerSettings.share_collection,
        passwordText: {
          ...get(accountSettings, "librarySettings.passwordText", {}),
          placeHolder: isEmpty(passwordText?.placeHolder)
            ? localizedStringPasswordProtected.placeholder
            : passwordText.placeHolder,
          heading: get(passwordText, 'heading',""),
          descriptionText: get(passwordText, 'descriptionText',""),
          buttonText: isEmpty(passwordText?.buttonText)
            ? localizedCommons.continue.toUpperCase()
            : passwordText.buttonText,
        },
        gatingText: {
          ...get(accountSettings, "librarySettings.gatingText", {}),
          placeHolder: isEmpty(gatingText?.placeHolder)
            ? localizedStringGatingCustomization.placeholder
            : gatingText.placeHolder,
          heading: get(gatingText, 'heading',""),
          descriptionText: get(gatingText, 'descriptionText',""),
        buttonText: isEmpty(gatingText?.buttonText)
          ? localizedCommons.continue.toUpperCase()
          : gatingText.buttonText,
        },
        ndaText: {
          ...get(accountSettings, "librarySettings.ndaText", {}),
          placeHolder: isEmpty(ndaText?.placeHolder)
            ? localizedStringNdaCustomization.placeholder
            : ndaText.placeHolder,
          heading: get(ndaText, 'heading',""),
          descriptionText: get(ndaText, 'descriptionText',""),
          buttonText: isEmpty(ndaText?.buttonText)
            ? localizedCommons.continue.toUpperCase()
            : ndaText.buttonText,
        },
        otpText: {
          ...get(accountSettings, "librarySettings.otpText", {}),
          heading: get(otpText, 'heading',""),
          descriptionText: get(otpText, 'descriptionText',""),
          buttonText: isEmpty(otpText?.buttonText) ?
            localizedCommons.continue.toUpperCase()
            : otpText.buttonText,
        },
        assetViewer: get(accountSettings, "librarySettings.assetViewer") || defaultViewersEnum.LIST_VIEWER,
        reshareEmail: get(accountSettings, "librarySettings.reshareEmail") || defaultEmailContent(),
        banner: get(accountSettings, "librarySettings.banner"),
        message: get(accountSettings, "librarySettings.message") || "<p></p>",
      },
    }
  }

  useEffect( () => {
     if(size(account) > 0){
        const initialSettings = getAccountSettings(account as any);
        initialAccountSettingsRef.current = initialSettings; // align the latest account settings with the initial account settings reference
        setAccountSettings(initialAccountSettingsRef.current);
      }
  }, [account]);


  useEffect(() => {
      if(resetEditors){
        if(!resetEditorsRef.current) return;
        resetEditorsRef.current.reset();
        setResetEditors(false); // reset the trigger state
      }
  }, [resetEditors])

  const getUpdatedCTAColor = (primaryColor: any, buttonTextColor: any): string => {
    if(!accountSettings.librarySettings || (!primaryColor && !buttonTextColor)) return "";

    let html = accountSettings.librarySettings.reshareEmail as string;

    let parser = new DOMParser();
    let doc = parser.parseFromString(html, "text/html");
    let ctaButtons = doc.querySelectorAll(".widget a") as NodeListOf<HTMLAnchorElement>

    ctaButtons.forEach((ctaButton) => {
      if(primaryColor){
        ctaButton.style.background = primaryColor;
        ctaButton.style.border = `10px solid ${primaryColor}`;
      }
      if(buttonTextColor){
        ctaButton.style.color = buttonTextColor;
      }
    });

    return doc.body.innerHTML;
  }

  const handleChange = (field: string, value: any) => { // change handler
    setAccountSettings((prev) => {
        const updatedAccountSettings = cloneDeep(prev); // clone prev state
        set(updatedAccountSettings, field, value); // set the value
        return updatedAccountSettings;
    });
  }

  const isThemeColorsModified = () => { // check if theme is modified by comparing the primary color and button text color with the initial account settings reference
    return !isEqual(get(initialAccountSettingsRef.current, 'librarySettings.primaryColor', ''), get(accountSettings, 'librarySettings.primaryColor', '')) 
         || !isEqual(get(initialAccountSettingsRef.current, 'librarySettings.buttonTextColor', ''), get(accountSettings, 'librarySettings.buttonTextColor', ''))
  }

  const handleSave = async () => { // Save
      let updateAccountSettings = {};

      let librarySettings = get(accountSettings, 'librarySettings', {});
      let favIcon =  get(accountSettings, 'favIcon', null) as MediaFile | null;

      if(!isEmpty(librarySettings)){

          if(isThemeColorsModified()){ // check theme colors modified
            const updatedReshareContent =  getUpdatedCTAColor(get(librarySettings, 'primaryColor', false), get(librarySettings, 'buttonTextColor', false));

            librarySettings = {
              ...librarySettings,
              reshareEmail: updatedReshareContent,
            }
          }

          let banner = {
            ...get(librarySettings, 'banner', null) as Banner | undefined,
          };
          
         if(banner){ // update banner background
               if(isBannerModified){
                   const bannerbackground = get(librarySettings, 'banner.background', undefined) as MediaFile | undefined;
                   const bannerBase64 = await convertBannerToBase64(bannerbackground); 
                   const updateBanner = {
                    ...banner,
                    'background': bannerBase64,
                    'mediaType': bannerbackground?.type,
                   };
                   librarySettings = {
                    ...librarySettings,
                    'banner': updateBanner
                   };
               } else {
                 delete banner['background'];
                 delete banner['mediaType'];
                 librarySettings = {
                    ...librarySettings,
                    'banner': banner,
                }
               } 
          }

          updateAccountSettings = {
           'librarySettings': librarySettings,
          }
      }

      if(favIcon && isFavIconModified){
        const favIconBase64 = await convertBannerToBase64(favIcon);
        updateAccountSettings = {
         ...updateAccountSettings,
          favIcon: favIconBase64,
        }
      }

      if(!isEmpty(updateAccountSettings)){
          const account = {
              ...updateAccountSettings,
          } as Partial<Account>;
          updateAccount(account, {
               onSuccess: (response) => {
                dispatch(updatingAccountSucceeded(response));
                toast(<Toast.Sucess heading="Success" message="Updated Sucessfully" />,{autoClose: 3000,});
               },
          }); // api call
      }
  }

  const handleReset = () => { // reset
    if(size(account) > 0){ // check if account data is present
      setAccountSettings(initialAccountSettingsRef.current); // reset to latest account settings reference
      setResetEditors(true);  // reset the input editors
    }
  }

  const viewerConfiguration = [ // viewer configuration
    {
       tabOrder: tabOrders[viewerSettingsEnum.themeAndGating],
      component: <ThemeAndGatingViewer
         data={accountSettings}
         handleChange={handleChange} 
         showEditor={showEditor}
         toggleEditor={() => {
          setShowEditor(!showEditor)
          }}
          ref={resetEditorsRef}
         />
    },{
      tabOrder: tabOrders[viewerSettingsEnum.library],
      component: <LibrarySettings
         data={accountSettings}
         handleChange={handleChange} 
         showEditor={showEditor}
         toggleEditor={() => {
          setShowEditor(!showEditor)
          }}
      />,
    },{
     tabOrder: tabOrders[viewerSettingsEnum.defaultViewer],
      component: <DefaultViewer 
                  data={accountSettings}
                  handleChange={handleChange} 
                  showEditor={showEditor}
                  toggleEditor={() => {
                      setShowEditor(!showEditor)
                }}/>,
                 
    },{
      tabOrder: tabOrders[viewerSettingsEnum.emailContent],
      component: <EmailViewer
                  data={accountSettings}
                  handleChange={handleChange} 
                  showEditor={showEditor}
                  toggleEditor={() => {
                    setShowEditor(!showEditor)
                  }}
      />     
    }
  ];

  return (
  ( !isAccountSettingsLoading && !isEmpty(accountSettings) && !isEmpty(accountSettings.librarySettings) ? 
   <div className={cx(styles.container)}>
        {/* Header */}
         <div className={styles.header}>
               <div className={styles.content}>
                 <h6 className={styles.title}>Viewer Settings</h6>
                <p className={styles.subText}>Customize how your shared content looks and behaves for viewers.</p>
               </div>

              <div className={styles.actionContainer}>
                 
                {previewActionTransitions((style, item) => {
                      if(!item) return null;
                      return (
                        <animated.div style={style}>
                           <Actions.Button
                                  width={2}
                                  icon={"paperflite-capture"}
                                  title="Preview"
                                  className={cx({ [styles.hidePreview]: !showEditor })}
                                  onClick={() => {
                                      setShowEditor(!showEditor);
                                  }}
                            />    
                        </animated.div>
                      );
                  })}
                                <Button
                                  variant="primary"
                                  icon="paperflite-tick"
                                  text={isPending ? "Saving..." : "Save"}
                                  disabled={isPending}
                                  onClick={handleSave}
                                />
                                <Button
                                  variant="secondary"
                                  text="Reset"
                                  onClick={handleReset}
                                />     
                 </div>

          </div>
            
          {/* Tabs & Content */}
          <div className={styles.innnerContainer}>
            <div className={styles.section}>
              <TabBar active={activeTab} onSelectionChange={(val) =>{
                  setShowEditor(true);
                  setActiveTab(val);
                }} gap={32}>
                {map(listOfTabs, (tab, index) => (
                  <div
                    key={tab.name+index}
                    className={cx(styles.tab, { [styles.active]: activeTab === index })}>
                    {tab.name}
                  </div>))}
              </TabBar>
            </div>

            {/* Viewer */}
            <div className={styles.viewerContainer} onFocus={(e) => e.preventDefault()}>
                 {viewerConfiguration.map((viewer) => {
                    return <>
                      <TabContent index={viewer.tabOrder} selected={activeTab}>
                        {viewer.component}
                      </TabContent>
                    </>
                 })}
             </div>
          </div>

   </div> : 
   <div className={cx(styles.loading)}>
      <p>Loading...</p>
    </div>)
  );
}

export const TabContent: React.FC<TabContentProps> = ({
  index, selected, children,
}) => {
  const direction = selected > index ? -1 : 1; // direction based on current selected and index direction left or right travels
  
  const transitions = useSpring({
    opacity: selected === index ? 1 : 0,
    transform:
      selected === index
        ? "translateX(0%)"
        : `translateX(${direction * 100}%)`, 
    config: { tension: 220, friction: 25 },
  });

  return (
    <animated.div
      className={cx(styles.viewerSection)}
      style={{
        ...transitions,
        pointerEvents: selected === index ? "auto" : "none", // prevent edit on hidden panels
      }}
    >
      {children}
    </animated.div> 
  );
};

export default withErrorBoundary(ViewerSettings); 